// Admin Service - GraphQL operations for Admin role
import { graphqlClient } from '../graphqlClient';

// ==================== Engine Operations ====================

export const getAllEngines = async () => {
  const query = `
    query GetAllEngines {
      getAllEngines {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getAllEngines;
};

export const getEngineById = async (id) => {
  const query = `
    query GetEngineById($id: String!) {
      getEngineById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        allowedProviders
        maxResellers
        maxMerchants
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.getEngineById;
};

export const getMyCreatedEngines = async () => {
  const query = `
    query GetMyCreatedEngines {
      getMyCreatedEngines {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getMyCreatedEngines;
};

export const createEngine = async (input) => {
  const mutation = `
    mutation CreateEngine($input: CreateEngineInput!) {
      createEngine(input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.createEngine;
};

export const updateEngine = async (id, input) => {
  const mutation = `
    mutation UpdateEngine($id: String!, $input: UpdateEngineInput!) {
      updateEngine(id: $id, input: $input) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        allowedProviders
        maxResellers
        maxMerchants
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateEngine;
};

export const deleteEngine = async (id) => {
  const mutation = `
    mutation DeleteEngine($id: String!) {
      deleteEngine(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteEngine;
};

export const changeEnginePassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeEnginePassword($input: ChangePasswordInput!) {
      changeEnginePassword(input: $input)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input: { id, newPassword } });
  return result.changeEnginePassword;
};

// ==================== Reseller Operations ====================

export const getAllResellers = async () => {
  const query = `
    query GetAllResellers {
      getAllResellers {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getAllResellers;
};

export const getResellerById = async (id) => {
  const query = `
    query GetResellerById($id: String!) {
      getResellerById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.getResellerById;
};

export const getMyCreatedResellers = async () => {
  const query = `
    query GetMyCreatedResellers {
      getMyCreatedResellers {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getMyCreatedResellers;
};

export const createReseller = async (input) => {
  const mutation = `
    mutation CreateReseller($input: CreateResellerInput!) {
      createReseller(input: $input) {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.createReseller;
};

export const updateReseller = async (id, input) => {
  const mutation = `
    mutation UpdateReseller($id: String!, $input: UpdateResellerInput!) {
      updateReseller(id: $id, input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateReseller;
};

export const deleteReseller = async (id) => {
  const mutation = `
    mutation DeleteReseller($id: String!) {
      deleteReseller(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteReseller;
};

export const changeResellerPassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeResellerPassword($input: ChangePasswordInput!) {
      changeResellerPassword(input: $input)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input: { id, newPassword } });
  return result.changeResellerPassword;
};

// ==================== Merchant Operations ====================

export const getAllMerchants = async () => {
  const query = `
    query GetAllMerchants {
      getAllMerchants {
        id
        code
        email
        name
        status
        callbackUrl
        webhookUrl
        createdByAdminId
        createdByEngineId
        createdByResellerId
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getAllMerchants || [];
};

export const getMerchantById = async (id) => {
  const query = `
    query GetMerchantById($id: String!) {
      getMerchantById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        apiKey
        secretKey
        callbackUrl
        webhookUrl
        createdByAdminId
        createdByEngineId
        createdByResellerId
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.getMerchantById;
};

export const getMyCreatedMerchants = async () => {
  const query = `
    query GetMyCreatedMerchants {
      getMyCreatedMerchants {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getMyCreatedMerchants;
};

export const createMerchant = async (input) => {
  const mutation = `
    mutation CreateMerchant($input: CreateMerchantInput!) {
      createMerchant(input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
        createdByAdminId
        createdByEngineId
        createdByResellerId
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.createMerchant;
};

export const updateMerchant = async (id, input) => {
  const mutation = `
    mutation UpdateMerchant($id: String!, $input: UpdateMerchantInput!) {
      updateMerchant(id: $id, input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateMerchant;
};

export const deleteMerchant = async (id) => {
  const mutation = `
    mutation DeleteMerchant($id: String!) {
      deleteMerchant(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteMerchant;
};

export const changeMerchantPassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeMerchantPassword($input: ChangePasswordInput!) {
      changeMerchantPassword(input: $input)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input: { id, newPassword } });
  return result.changeMerchantPassword;
};

// ==================== Dashboard Stats ====================

export const getAdminDashboardStats = async () => {
  const query = `
    query GetAdminDashboardStats {
      getAdminDashboardStats {
        totalEngines
        totalResellers
        totalMerchants
        myCreatedEngines
        myCreatedResellers
        myCreatedMerchants
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getAdminDashboardStats;
};

export const getGlobalDashboardStats = async () => {
  const query = `
    query GetGlobalDashboardStats {
      getGlobalDashboardStats {
        totalEngines
        totalResellers
        totalMerchants
        activeEngines
        activeResellers
        activeMerchants
        inactiveEngines
        inactiveResellers
        inactiveMerchants
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getGlobalDashboardStats;
};

// ==================== Admin Operations ====================

export const getAllAdmins = async () => {
  const query = `
    query GetAllAdmins {
      getAllAdmins {
        id
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getAllAdmins;
};

export const createAdmin = async (input) => {
  const mutation = `
    mutation CreateAdmin($input: CreateAdminInput!) {
      createAdmin(input: $input) {
        id
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.createAdmin;
};

// ==================== Staff Operations ====================

export const getMyStaffUsers = async () => {
  const query = `
    query GetMyStaffUsers {
      getMyStaffUsers {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
          panelType
          permissions
          isSystem
        }
        permissions
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getMyStaffUsers;
};

export const getStaffUserById = async (id) => {
  const query = `
    query GetStaffUserById($id: String!) {
      getStaffUserById(id: $id) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
          panelType
          permissions
          isSystem
        }
        permissions
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.getStaffUserById;
};

export const getAvailableRoles = async () => {
  const query = `
    query GetAvailableRoles {
      getAvailableRoles {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.getAvailableRoles;
};

export const createStaffUser = async (input) => {
  const mutation = `
    mutation CreateStaffUser($input: CreateStaffUserInput!) {
      createStaffUser(input: $input) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
        }
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.createStaffUser;
};

export const updateStaffUser = async (id, input) => {
  const mutation = `
    mutation UpdateStaffUser($id: String!, $input: UpdateStaffUserInput!) {
      updateStaffUser(id: $id, input: $input) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
        }
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateStaffUser;
};

export const deleteStaffUser = async (id) => {
  const mutation = `
    mutation DeleteStaffUser($id: String!) {
      deleteStaffUser(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteStaffUser;
};

export const assignRoleToStaff = async (input) => {
  const mutation = `
    mutation AssignRoleToStaff($input: AssignRoleInput!) {
      assignRoleToStaff(input: $input) {
        id
        email
        name
        roles {
          id
          name
          description
        }
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.assignRoleToStaff;
};

export const removeRoleFromStaff = async (input) => {
  const mutation = `
    mutation RemoveRoleFromStaff($input: AssignRoleInput!) {
      removeRoleFromStaff(input: $input) {
        id
        email
        name
        roles {
          id
          name
          description
        }
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.removeRoleFromStaff;
};

export const createStaffRole = async (input) => {
  const mutation = `
    mutation CreateStaffRole($input: CreateStaffRoleInput!) {
      createStaffRole(input: $input) {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.createStaffRole;
};

// ==================== Webhook Logs ====================

export const getWebhookLogs = async (limit) => {
  const query = `
    query GetWebhookLogs($limit: Int) {
      getWebhookLogs(limit: $limit) {
        id
        qrCodeId
        paymentId
        merchantId
        merchantName
        event
        url
        httpStatus
        responseBody
        errorMessage
        requestPayload
        createdAt
      }
    }
  `;
  const result = await graphqlClient.query(query, limit != null ? { limit } : {});
  return result.getWebhookLogs || [];
};

